import { InternalAccessor } from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { VatDeclarationStatus } from '@views/vatDeclarations/enums/vatDeclarationStatus'

import { TaxReturn } from '../../../../../types/taxReturn'
import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { useVatDeclarationsRoutes } from '../../../context/vatDeclarationsRouteContext'
import { VatDeclarationSubmission } from '../../../types/vatDeclarationSubmission'
import { formatAmountWithCurrency } from '../../../utils/formatAmountWithCurrency'
import { getPeriodData } from '../../../utils/getPeriodData'
import { getVatDeclarationStatus } from '../../../utils/getVatDeclarationStatus'
import { VatDeclarationsTableAccessor } from '../enums/vatDeclarationsTableAccessor'
import { VatDeclarationsTableRow } from '../types/vatDeclarationTableRow'
import { getVatDeclarationDescription } from '../utils/getVatDeclarationDescription'
import { getVatDeclarationsTableStaticActions } from '../utils/getVatDeclarationsTableStaticActions'

interface UseVatDeclarationsTableDataParams {
  vatDeclarations: TaxReturn[]
  currency?: string
  locale?: string
  submissionsMap?: Record<string, VatDeclarationSubmission>
  countryId?: string
  isSkatConnected?: boolean
}

export const useVatDeclarationsTableData = ({
  vatDeclarations,
  currency = 'EUR',
  locale = 'nl_NL',
  submissionsMap,
  countryId,
  isSkatConnected,
}: UseVatDeclarationsTableDataParams): VatDeclarationsTableRow[] => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const organizationUrl = organization?.url
  const vatDeclarationsRoutes = useVatDeclarationsRoutes()

  return useMemo(
    () =>
      vatDeclarations.map(
        ({
          correctionNo,
          id,
          isPaid,
          isPayable,
          isSettled,
          endDate,
          period,
          periodText,
          reportDeadline,
          settledAmount,
        }) => {
          const amountToDisplay = settledAmount
            ? formatAmountWithCurrency({
                amount: settledAmount,
                locale,
                currency,
              })
            : ''

          const submission = submissionsMap?.[id]
          const vatDeclarationStatus = getVatDeclarationStatus({
            isSettled,
            isPaid,
            isPayable,
            periodEndDate: endDate,
            reportDeadline,
            submissionStatus: submission?.status,
          })

          const staticActionsItems = getVatDeclarationsTableStaticActions({
            countryId,
            correctionNo,
            isPaid,
            isSettled,
            isSkatConnected,
            submission,
            status: vatDeclarationStatus,
            t,
          })
          const description = getVatDeclarationDescription({
            reportDeadline,
            vatDeclarationStatus,
            t,
          })
          const shouldShowHoverActions =
            vatDeclarationStatus === VatDeclarationStatus.ReadyToPay ||
            vatDeclarationStatus === VatDeclarationStatus.OverduePayment

          const viewVatDeclarationUrl = getOrganizationViewUrl(
            organizationUrl,
            vatDeclarationsRoutes.getViewVatDeclarationRoute(id),
          )

          return {
            currentCorrection: typeof correctionNo === 'number' ? { correctionNo } : undefined,
            id,
            [VatDeclarationsTableAccessor.Status]: vatDeclarationStatus,
            [VatDeclarationsTableAccessor.PeriodName]: periodText,
            [VatDeclarationsTableAccessor.Description]: description,
            [VatDeclarationsTableAccessor.Result]: amountToDisplay,
            [InternalAccessor.UrlInternal]: viewVatDeclarationUrl,
            [InternalAccessor.HoverActions]: shouldShowHoverActions,
            [InternalAccessor.StaticActions]: true,
            periodData: getPeriodData(period),
            staticActionsItems,
          }
        },
      ),
    [
      countryId,
      currency,
      isSkatConnected,
      locale,
      organizationUrl,
      submissionsMap,
      t,
      vatDeclarations,
      vatDeclarationsRoutes,
    ],
  )
}
